import * as React from 'react'
import moment from 'moment'

import { ExportType } from '@/app/constants/exportType'
import { getExportNumberByType } from '@/app/services/export/exportTypeResolver'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { isDriver } from '@/app/services/local/roleService'
import { FaIcon, faPrint, faUsers } from '@/lib/fa'
import { useGenerateCsv } from '../services/useExportService'
import { useExport } from '@/modules/exports/stores/exportStore'
import { RouteComponentProps } from '@/app/navigation/withRouter'
import { useImiSidePanel } from '../context/ImiSidePanelContext'
import { useBfmPayrollAvailableQuery } from '@/lib/api/company/timesheet/bfmPayrollAvailable/useBfmPayrollAvailableQuery'
import { TaskStatus } from '@/modules/shared/components'

type MatchParams = {
  companyId: string
}

type ExportItem = {
  name: string
  type: ExportType
  disabled: boolean
}

const LIST = [
  {
    group: 'exportList.title',
    reports: [
      {
        name: 'exportList.paymentSapExport',
        type: ExportType.PaymentSapExport,
        disabled: false,
      },
      {
        name: 'exportList.paymentMerinosoftExport',
        type: ExportType.PaymentMerinosoftExport,
        disabled: false,
      },
      {
        name: 'exportList.paymentMerinosoftWorkOverPlanExport',
        type: ExportType.PaymentMerinosoftWorkOverPlanExport,
        disabled: false,
      },
      {
        name: 'exportList.exportTimesheetToR2Payer',
        type: ExportType.ExportTimesheetToR2Payer,
        disabled: false,
      },
      {
        name: 'exportList.exportTimesheetV2ToR2Payer',
        type: ExportType.ExportTimesheetV2ToR2Payer,
        disabled: false,
      },
      {
        name: 'exportList.exportTimesheetAmountsToR2Payer',
        type: ExportType.ExportTimesheetAmountsToR2Payer,
        disabled: false,
      },
      {
        name: 'exportList.exportTimesheetToEnova',
        type: ExportType.ExportTimesheetToEnova,
        disabled: false,
      },
      {
        name: 'exportList.exportTimesheetToEnovaSecondVersion',
        type: ExportType.ExportTimesheetToEnovaSecondVersion,
        disabled: false,
      },
      {
        name: 'exportList.exportFullTimesheetToEnova',
        type: ExportType.ExportFullTimesheetToEnova,
        disabled: false,
      },
      {
        name: 'exportList.exportForeignWorkToEnova',
        type: ExportType.ExportForeignWorkToEnova,
        disabled: false,
      },
      {
        name: 'exportList.exportForeignTripsToEnova',
        type: ExportType.ExportForeignTripsToEnova,
        disabled: false,
      },
      {
        name: 'exportList.exportTimesheetToOptima',
        type: ExportType.OptimaTimesheetExportCsv,
        disabled: false,
      },
      {
        name: 'exportList.exportTripsToOptima',
        type: ExportType.OptimaBasicTripsExportCsv,
        disabled: false,
      },
      {
        name: 'exportList.optimaTimesheetAmountsExport',
        type: ExportType.OptimaTimesheetAmountsExport,
        disabled: false,
      },
      {
        name: 'exportList.foreignAllowancesOptimaExport',
        type: ExportType.ForeignAllowancesOptimaExport,
        disabled: false,
      },
      {
        name: 'exportList.homelandAllowancesOptimaExport',
        type: ExportType.HomelandAllowancesOptimaExport,
        disabled: false,
      },
      {
        name: 'exportList.optimaFreeDaysExport',
        type: ExportType.OptimaFreeDaysExport,
        disabled: false,
      },
      {
        name: 'exportList.symfoniaTripsExport',
        type: ExportType.SymfoniaTripsExport,
        disabled: false,
      },
    ],
  },
]

const LIST_2 = [
  {
    group: null,
    reports: [
      {
        name: 'exportList.exportDriversToExternalSystem',
        type: ExportType.ExportDriversToExternalSystem,
        disabled: false,
      },
      {
        name: 'exportList.exportDriverFreeDays',
        type: ExportType.ExportDriverFreeDays,
        disabled: false,
      },
      {
        name: 'exportList.driversListExport',
        type: ExportType.DriversListExport,
        disabled: false,
      },
      {
        name: 'exportList.pretorExport',
        type: ExportType.PretorExport,
        disabled: false,
      },
      {
        name: 'exportList.exportDriversToImi',
        type: ExportType.ExportDriversToImi,
        disabled: false,
      },
      {
        name: 'exportList.sapPccXmlExport',
        type: ExportType.SapPccXmlExport,
        disabled: false,
      },
      {
        name: 'exportList.foreignAllowancesXlsxExport',
        type: ExportType.ForeignAllowancesXlsxExport,
        disabled: false,
      },
      {
        name: 'exportList.absenceExportToR2Payer',
        type: ExportType.AbsenceExportToR2Payer,
        disabled: false,
      },
      {
        name: 'exportList.universalTripPartAmountsExport',
        type: ExportType.UniversalTripPartAmountsExport,
        disabled: false,
      },
      {
        name: 'exportList.sapTimesheetMabauExport',
        type: ExportType.SapTimesheetMabauExport,
        disabled: false,
      },
      {
        name: 'exportList.sapTimesheetDaysMabauExport',
        type: ExportType.SapTimesheetDaysMabauExport,
        disabled: false,
      },
      {
        name: 'exportList.sapTimesheetScheduleMabauExport',
        type: ExportType.SapTimesheetScheduleMabauExport,
        disabled: false,
      },
      {
        name: 'exportList.tripInvoiceExport',
        type: ExportType.TripInvoiceExport,
        disabled: false,
      },
      {
        name: 'exportList.gratyfikantExport',
        type: ExportType.GratyfikantExport,
        disabled: false,
      },
      {
        name: 'exportList.waproGangSanitarityExport',
        type: ExportType.WaproGangSanitarityExport,
        disabled: false,
      },
    ],
  },
]

const ReportsListPageContent: React.FC<RouteComponentProps<MatchParams>> = ({
  match,
}) => {
  const { t } = useLocalize()
  const { params } = match
  const { companyId } = params

  const { dateRange } = useExport()
  const endDate = dateRange?.endDate
  const month = endDate
    ? `${endDate.year}-${String(endDate.month).padStart(2, '0')}`
    : null
  const generateCsvMutation = useGenerateCsv(companyId)
  // Eksport "Kwoty wysylane do BFM" pokazujemy tylko tam, gdzie wysylka BFM jest wlaczona
  // (dzis: jedna firma). Gate po fladze z serwera, nie po nazwie/ID firmy.
  const bfmAvailable = useBfmPayrollAvailableQuery(companyId)
  // group: null — BEZ naglowka. Naglowek grupy ma cursor-pointer i podswietlenie na hover,
  // ale nie ma zadnego onClick: przy jednej pozycji uzytkownik klika w naglowek, nic sie nie
  // dzieje i wyglada to na zepsuty przycisk. Jako zwykly wiersz zachowuje sie jak reszta listy.
  const bfmGroups = React.useMemo(
    () =>
      bfmAvailable.data?.available
        ? [
            {
              group: null,
              reports: [
                {
                  name: 'exportList.bfmPayrollPreview',
                  type: ExportType.BfmPayrollPreview,
                  disabled: false,
                },
              ],
            },
          ]
        : [],
    [bfmAvailable.data?.available],
  )
  const { selectedDriverIds } = useImiSidePanel()

  const getCheckedDrivers = (checkedDrivers: ReadonlyArray<string>) => {
    let drivers = []

    const driver = isDriver(companyId)
    if (driver) {
      // For driver users, we'd need driverId from route params
      // For now, return empty array
      drivers = []
    } else if (checkedDrivers != null && checkedDrivers.length > 0) {
      drivers = [...checkedDrivers]
    }

    return drivers
  }

  const handleExportClick = async (report: ExportItem) => {
    const drivers = getCheckedDrivers(selectedDriverIds)
    if (drivers.length > 0 && !report.disabled && month) {
      const monthMoment = moment(month + '-01')
      await generateCsvMutation.mutateAsync({
        reportType: getExportNumberByType(report.type),
        drivers,
        date: monthMoment,
      })
    }
  }

  const renderExportGroup = (group: any, index: number) => {
    const drivers = getCheckedDrivers(selectedDriverIds)

    return (
      <div
        key={index}
        className="tw-flex tw-h-fit tw-flex-col  tw-shadow-sm"
      >
        {/* Group Header */}
        {group.group != null && (
          <div
            className={`tw-flex  tw-cursor-pointer tw-items-center tw-gap-2 tw-border-b tw-border-secondary/50 tw-bg-[#FAFBFF] tw-p-3 tw-transition-all hover:tw-bg-primary/5`}
          >
            <FaIcon
              icon={faUsers}
              className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-content-base"
            />
            <span
              className={`tw-flex-1 tw-break-words tw-text-left tw-text-xs tw-font-medium tw-text-content-base`}
            >
              {t(group.group)}
            </span>
          </div>
        )}

        {/* Reports List */}
        <div className="tw-flex tw-flex-col ">
          {group.reports.map((report: ExportItem, reportIndex: number) => {
            const isEnabled =
              drivers.length > 0 && !report.disabled && month !== null

            return (
              <div
                key={reportIndex}
                onClick={() =>  isEnabled && handleExportClick(report)}
                className={`tw-flex tw-cursor-pointer tw-items-start tw-gap-2 tw-border-b tw-border-secondary/50 tw-bg-[#FAFBFF] tw-p-3 tw-transition-all last:tw-border-b-0 ${
                  isEnabled
                    ? 'hover:tw-bg-primary/5'
                    : 'tw-cursor-not-allowed tw-opacity-30'
                }`}
              >
                <FaIcon
                  icon={faPrint}
                  className="tw-mt-0.5 tw-h-3 tw-w-3 tw-flex-shrink-0 tw-text-content-base"
                  title={t('operation.get-pdf')}
                />
                <span
                  className={`tw-flex-1 tw-break-words tw-text-left tw-text-xs tw-font-medium ${
                    report.disabled ? 'tw-opacity-30' : 'tw-text-content-base'
                  }`}
                >
                  {t(report.name)}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1">
      {/* Bez tego eksport nie konczy sie niczym: zadanie leci, plik powstaje, ale
          pobiera go dopiero handler w TaskStatus - a ten ekran go nie montowal. */}
      <div className="tw-absolute tw-right-0 tw-top-0 tw-z-[101]">
        <TaskStatus />
      </div>

      <div className="tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-y-auto">
        {LIST.map((g, i) => renderExportGroup(g, i))}
        {LIST_2.map((g, i) => renderExportGroup(g, i + LIST.length))}
        {bfmGroups.map((g, i) =>
          renderExportGroup(g, i + LIST.length + LIST_2.length),
        )}
      </div>
    </section>
  )
}

const ReportsListPage: React.FC<RouteComponentProps<MatchParams>> = (props) => {
  return <ReportsListPageContent {...props} />
}

export default ReportsListPage
export { ReportsListPage }
